import React, { useEffect, useRef, useState, useCallback, useMemo } from "react";
import { useParams } from "react-router-dom";
import { Box, Flex, useToast, Button, Text, HStack, useDisclosure, Modal, ModalOverlay, ModalContent, ModalHeader, ModalFooter, ModalBody, ModalCloseButton } from "@chakra-ui/react";
import {
  getFilmEditorialData,
  updateFilmEditorialData,
  updateFilmImagesData,
  deleteFilmImagesData,
} from "../../../services/Films";
import Form from "@rjsf/chakra-ui";
import validator from "@rjsf/validator-ajv8";
import HotTable from "@handsontable/react";
import "handsontable/dist/handsontable.full.min.css";
import "../../../styles/handsontable.css"; // Import generic HandsOnTable styles
import SubmitButton from '../../../CommonComponents/SubmitButton';
import { getImageURL } from "../../../util/getImage";
import ImageModal from "../../../FunctionalComponents/Modal/ImageModal";
import { RjsfSubmitButton } from "../../../RJSFCommonSchemas";
import FormLoader from "../../../CommonComponents/FormLoader";
import Pagination from "../../DeriveFile/Pagination";
import { showDeletedToast, showErrorToast, showInfoToast, showSuccessToast } from "../../../util/errorHandler";


const Editorial = ({ onCountLoad }) => {
  const { alias } = useParams();
  const [basicFieldData, setBasicFieldData] = useState({});
  // const [imageData, setImageData] = useState({});
  const [columnHeaders, setColumnHeaders] = useState([]);
  const [tableData, setTableData] = useState([]);
  const [originalData, setOriginalData] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [modalImageSrc, setModalImageSrc] = useState("");
  const [quickSave, setQuickSave] = useState(false);
  const [changedRecords, setChangedRecords] = useState(new Map()); // Track changes by row index
  const [deletingRows, setDeletingRows] = useState(new Set()); // Track rows being deleted
  const [rowToDelete, setRowToDelete] = useState(null); // Track which row to delete
  const [arrayColumns, setArrayColumns] = useState([]); // Track which columns are arrays
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(10);
  const [totalRecords, setTotalRecords] = useState(0);
  const [allData, setAllData] = useState([]); // Store all data for pagination

  const hotRef = useRef(null);
  const toast = useToast();
  const { isOpen, onOpen, onClose } = useDisclosure();

  // Helper function to identify array columns
  const getArrayColumns = useCallback((sampleData) => {
    const arrayColumns = [];
    if (sampleData && sampleData.length > 0) {
      const firstRow = sampleData[0];
      Object.keys(firstRow).forEach(key => {
        if (Array.isArray(firstRow[key])) {
          arrayColumns.push(key);
        }
      });
    }
    return arrayColumns;
  }, []);

  // Handle individual cell changes in HandsOnTable
  const handleTableChange = useCallback((changes, source) => {
    if (source === 'edit' && changes) {
      const newChangedRecords = new Map(changedRecords);
      
      changes.forEach(([row, col, oldValue, newValue]) => {
        // Skip S.No. column (col === 0), ID column (col === 1), and Actions column
        if (col === 0 || col === 1 || col === columnHeaders.length + 1) return;
        
        // Only track if value actually changed
        if (oldValue !== newValue) {
          const rowKey = row;
          const columnHeader = columnHeaders[col - 1]; // Adjust index due to S.No. column
          
          // Convert comma-separated strings back to arrays for array-type columns
          let processedValue = newValue;
          if (typeof newValue === 'string' && newValue.includes(',') && arrayColumns.includes(columnHeader)) {
            // Convert comma-separated string back to array for known array columns
            processedValue = newValue.split(',').map(item => item.trim()).filter(item => item !== '');
          }
          
          if (!newChangedRecords.has(rowKey)) {
            // Initialize record with ID and changed field
            newChangedRecords.set(rowKey, {
              [columnHeaders[0]]: tableData[row][1], // ID field (now at index 1)
              [columnHeader]: processedValue
            });
          } else {
            // Add/update field in existing changed record
            const existingRecord = newChangedRecords.get(rowKey);
            existingRecord[columnHeader] = processedValue;
            newChangedRecords.set(rowKey, existingRecord);
          }
        }
      });
      setChangedRecords(newChangedRecords);
    }
  }, [changedRecords, columnHeaders, tableData, arrayColumns]);

  // Pagination utility functions
  const getCurrentPageData = useCallback(() => {
    const startIndex = (currentPage - 1) * pageSize;
    const endIndex = startIndex + pageSize;
    return allData.slice(startIndex, endIndex);
  }, [currentPage, pageSize, allData]);

  const getTotalPages = useCallback(() => {
    return Math.ceil(totalRecords / pageSize);
  }, [totalRecords, pageSize]);

  const handlePageChange = useCallback((newPage) => {
    if (newPage >= 1 && newPage <= getTotalPages()) {
      setCurrentPage(newPage);
      // Clear changed records when changing pages
      setChangedRecords(new Map());
      
      // Update current page data
      const newPageData = getCurrentPageData();
      setTableData(newPageData);
      setOriginalData(newPageData);
    }
  }, [getTotalPages, getCurrentPageData]);

  const schema = {
    title: "Film Editorial fields",
    description: "",
    type: "object",
    properties: {
      editorial_text: {
        type: "string",
        title: "Editorial",
      },
    },
  };

  const uiSchema = {
    ...RjsfSubmitButton(isSubmitting),
    editorial_text: {
      "ui:widget": "textarea",
      "ui:emptyValue": ""
    },
  };

  const formData = basicFieldData;

  useEffect(() => {
    const getEditorialData = async () => {
      if (!alias) return;
  
      try {
        setIsLoading(true);
        const result = await getFilmEditorialData(alias);
        
        if (!result) {
          throw new Error('No data received from API');
        }
  
        const { images, ...basicFields } = result;
        
        // Initialize states with empty/default values if no image data
        if (!images?.data?.length) {
          // setImageData([]);
          setColumnHeaders([]);
          setTableData([]);
          setBasicFieldData(basicFields);
          setQuickSave(false);
          return;
        }
  
        // Process image data only if it exists
        const updatedData = images?.data?.map((item) => ({
          ...item,
          image: getImageURL(item?.image_alias[0], 200, 200),
        }));
  
        // Get column headers from first item
        const firstItem = updatedData[0];
        const keys = Object.keys(firstItem).filter(key => firstItem[key] != null);
        
        // Identify array columns
        const arrayColumns = getArrayColumns(updatedData);
        setArrayColumns(arrayColumns);
        
        // Ensure all required columns are present in the correct order
        const requiredColumns = ['_id', 'masterlist_alias', 'alias', 'image_alias', 'corrected_sequence', 'sr_no', '__v', 'image'];
        const orderedKeys = requiredColumns.filter(key => keys.includes(key));
        
        // Transform data for table with S.No. and Actions columns
        const rowData = updatedData.map(item => [
          null, // Placeholder for S.No. column (will be calculated dynamically)
          ...orderedKeys.map(key => {
            const value = item[key];
            
            // Handle array values by converting them to comma-separated strings
            if (Array.isArray(value)) {
              return value.join(', '); // Convert array to comma-separated string
            }
            
            return value ?? ""; // Extract values by key in correct order
          }), // Extract values by key in correct order
          null // Add placeholder for Actions column
        ]);
  
        // Store all data for pagination
        setAllData(rowData);
        setTotalRecords(rowData.length);
        
        // Set current page data
        const currentPageData = rowData.slice(0, pageSize);
        setTableData(currentPageData);
        setOriginalData(currentPageData); // Store original data for comparison
        
        // Update states
        // setImageData(images.data);
        setColumnHeaders(orderedKeys);
        setBasicFieldData(basicFields);
        setQuickSave(false);
  
      } catch (error) {
        const errorMessage = error.message || 'Failed to fetch film data';
        console.error('Error in getEditorialData:', errorMessage, error);
        
        showErrorToast(toast, errorMessage);
      } finally {
        setIsLoading(false);
      }
    };
  
    getEditorialData();
  }, [alias, toast, quickSave, pageSize, getArrayColumns]);

  useEffect(() => {
    if (onCountLoad) onCountLoad(totalRecords);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [totalRecords]);

  // Update table data when pagination changes
  useEffect(() => {
    if (allData.length > 0) {
      const currentPageData = getCurrentPageData();
      setTableData(currentPageData);
      setOriginalData(currentPageData);
    }
  }, [currentPage, pageSize, allData, getCurrentPageData]);

  const handleSubmit = async (basicFieldData) => {
    try {
      if (alias) {
        setIsSubmitting(true)
        const result = await updateFilmEditorialData(alias, basicFieldData);
        if (result?.status === 200) {
          setQuickSave(prev => !prev);
                    showSuccessToast(toast, "Data updated successfully");
        }
      }
    } catch (error) {
      console.error("Error updating film data:", error);
            showErrorToast(toast, "Failed to update film data");
    } finally {
      setIsSubmitting(false);
    }
  };

  const saveClickCallback = useCallback(async () => {
    // Check if there are any changes to save
    if (changedRecords.size === 0) {
            showInfoToast(toast, "No changes to save");
      return;
    }

    setIsSubmitting(true);
    
    try {
      // Convert Map to array of changed records
      const updatedRecords = Array.from(changedRecords.values());
      const result = await updateFilmImagesData(alias, updatedRecords);

      if (result?.status === 200) {
                showSuccessToast(toast, "Data saved successfully");
        
        // Clear the changed records after successful save
        setChangedRecords(new Map());
        
        // Update the original data to reflect the new state
        const hot = hotRef.current.hotInstance;
        const currentData = hot.getData();
        setOriginalData(currentData);
        
        // Update the table data to reflect changes immediately
        setTableData(currentData);
        
        // Update the corresponding data in allData to maintain consistency across pages
        const updatedAllData = [...allData];
        const startIndex = (currentPage - 1) * pageSize;
        currentData.forEach((row, index) => {
          updatedAllData[startIndex + index] = row;
        });
        setAllData(updatedAllData);
      }
    } catch (error) {
      console.error("Error saving data:", error);
            showErrorToast(toast, "Failed to save data. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  }, [changedRecords, alias, currentPage, pageSize, allData, toast]);

  const openImageModal = useCallback((src) => {
    setModalImageSrc(src);
    setIsModalOpen(true);
  }, []);

  const closeImageModal = () => {
    setIsModalOpen(false);
    setModalImageSrc("");
  };

  // Delete functionality
  const handleDeleteRow = useCallback(async (rowIndex) => {
    const rowData = tableData[rowIndex];
    const recordId = rowData[1]; // ID is now at index 1 (after S.No. column)
    
    if (!recordId) {
            showErrorToast(toast, "Cannot delete record without ID");
      return;
    }

    // Add row to deleting state
    setDeletingRows(prev => new Set(prev).add(rowIndex));
    
    try {
      const result = await deleteFilmImagesData(alias, [{ [columnHeaders[0]]: recordId }]);
      
      if (result?.status === 200) {
        // Calculate the global index of the deleted row
        const globalRowIndex = (currentPage - 1) * pageSize + rowIndex;
        
        // Remove row from all data sources
        const newAllData = allData.filter((_, index) => index !== globalRowIndex);
        
        const newTableData = tableData.filter((_, index) => index !== rowIndex);
        const newOriginalData = originalData.filter((_, index) => index !== rowIndex);
        
        // Update all states
        setAllData(newAllData);
        setTableData(newTableData);
        setOriginalData(newOriginalData);
        setTotalRecords(prev => prev - 1);
        
        // Clear any changes for this row
        const newChangedRecords = new Map(changedRecords);
        newChangedRecords.delete(rowIndex);
        setChangedRecords(newChangedRecords);
        
                showDeletedToast(toast);
        
        // Check if current page is now empty and adjust
        if (newTableData.length === 0 && currentPage > 1) {
          handlePageChange(currentPage - 1);
        }
      } else {
        throw new Error("Delete operation failed");
      }
    } catch (error) {
      console.error("Error deleting record:", error);
            showErrorToast(toast, "Failed to delete record. Please try again.");
    } finally {
      // Remove row from deleting state
      setDeletingRows(prev => {
        const newSet = new Set(prev);
        newSet.delete(rowIndex);
        return newSet;
      });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tableData, alias, columnHeaders, currentPage, pageSize, allData, changedRecords, handlePageChange, toast]);

  // Delete confirmation dialog
  const confirmDelete = useCallback((rowIndex) => {
    const rowData = tableData[rowIndex];
    const recordId = rowData[1]; // ID is now at index 1
    
    setRowToDelete({ rowIndex, recordId, rowData });
    onOpen();
  }, [tableData, onOpen]);

  // Handle actual deletion after confirmation
  const handleConfirmedDelete = useCallback(() => {
    if (rowToDelete) {
      handleDeleteRow(rowToDelete.rowIndex);
      onClose();
      setRowToDelete(null);
    }
  }, [rowToDelete, onClose, handleDeleteRow]);

  // Custom renderer for delete column
  const deleteColumnRenderer = useCallback((instance, td, row, col, prop, value, cellProperties) => {
    // Clear the cell
    td.innerHTML = '';
    
    // Create delete button
    const deleteButton = document.createElement('button');
    const isDeleting = deletingRows.has(row);
    
    if (isDeleting) {
      deleteButton.innerHTML = '⏳'; // Loading indicator
      deleteButton.title = 'Deleting...';
      deleteButton.className = 'delete-button deleting';
    } else {
      deleteButton.innerHTML = '🗑️'; // Delete icon
      deleteButton.title = 'Delete this record';
      deleteButton.className = 'delete-button';
    }
    
    // Add click handler
    deleteButton.addEventListener('click', (e) => {
      e.preventDefault();
      e.stopPropagation();
      if (!isDeleting) {
        confirmDelete(row);
      }
    });
    
    // Append button to cell
    td.appendChild(deleteButton);
    
    return td;
  }, [deletingRows, confirmDelete]);

  const hotSettings = useMemo(() => ({
    data: tableData,
    colHeaders: ['S.No.', ...columnHeaders, 'Actions'], // Add S.No. as first column
    height: "auto",
    afterChange: handleTableChange, // Track changes in real-time
    licenseKey: "non-commercial-and-evaluation",
    autoWrapRow: true,
    contextMenu: true,
    cells: (row, col) => {
      if (col === 0) return { readOnly: true }; // S.No. column is read-only
      if (col === columnHeaders.length + 1) return { renderer: deleteColumnRenderer }; // Delete column
      
      // Handle read-only columns based on header names
      if (col > 0 && col <= columnHeaders.length) {
        const headerName = columnHeaders[col - 1];
        if (headerName === '_id' || headerName === 'masterlist_alias' || headerName === 'alias' || headerName === '__v' ) {
          return { readOnly: true };
        }
        
        // Ensure array columns are editable
        if (arrayColumns.includes(headerName)) {
          return { 
            readOnly: false,
            allowInvalid: true, // Allow invalid values temporarily
            type: 'text' // Force text type for better editing
          };
        }
      }
      
      return {}; // Default for other columns
    },
    hiddenColumns: {
      columns: [1, 7], // Hide: _id (index 1), __v (index 6)
      indicators: false, // No visual indicators for hidden columns
    },
    plugins: ["hiddenColumns"],
    columns: [
      // S.No. column
      {
        data: 0,
        title: 'S.No.',
        width: 60,
        readOnly: true,
        renderer: (instance, td, row, col, prop, value, cellProperties) => {
          // Calculate serial number based on current page and row
          const serialNumber = (currentPage - 1) * pageSize + row + 1;
          td.innerHTML = serialNumber;
          td.style.textAlign = 'center';
          td.style.fontWeight = 'bold';
          td.style.backgroundColor = '#f8f9fa';
          td.style.borderRight = '2px solid #dee2e6';
          td.style.color = '#495057';
          td.style.height = '30px';
          return td;
        }
      },
      // Map existing columns (shifted by 1 due to S.No. column)
      // Column order: S.No. (0), _id (1), masterlist_alias (2), alias (3), image_alias (4), corrected_sequence (5), __v (6), sr_no (7), image (8), Actions (9)
      ...columnHeaders.map((header, index) => {
        const baseConfig = {
          data: index + 1, // Shift data index by 1
          title: header,
          readOnly: header === '_id' || header === 'masterlist_alias' || header === 'alias' || header === '__v' , // Make specific columns read-only
          // Ensure image_alias and corrected_sequence are editable
          className: header === 'image_alias' || header === 'corrected_sequence' || header === 'sr_no' ? 'editable-column' : ''
        };
        
        // Add special configuration for array columns
        if (arrayColumns.includes(header)) {
          baseConfig.allowInvalid = true;
          baseConfig.type = 'text';
          baseConfig.className = 'editable-column array-column';
        }
        
        // Add special renderer for image column
        if (header === 'image') {
          baseConfig.renderer = (instance, td, row, col, prop, value, cellProperties) => {
            if (value) {
              const img = document.createElement("img");
              img.src = value;
              img.style.width = "32px";
              img.style.height = "auto";
              img.style.display = "block";
              img.style.cursor = "pointer";
              
              img.addEventListener("click", () => {
                openImageModal(value);
              });
              
              td.innerText = "";
              td.appendChild(img);
            } else {
              td.innerText = "No Image";
            }
            return td;
          };
        }
        
        return baseConfig;
      }),
      // Add delete column
      {
        data: columnHeaders.length + 1,
        title: 'Actions',
        width: 80,
        renderer: deleteColumnRenderer
      }
    ],
    // Row styling for deleting rows
    beforeRenderer: (row, col, prop, value, cellProperties) => {
      if (deletingRows.has(row)) {
        cellProperties.className = 'deleting-row';
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }), [tableData, columnHeaders, handleTableChange, deleteColumnRenderer, currentPage, pageSize, deletingRows, openImageModal]);


  return (
    <Box maxW="100%">
      {isLoading ? (
        <FormLoader />
      ) : (
        <Form schema={schema} uiSchema={uiSchema} formData={formData} validator={validator} onSubmit={(e) => handleSubmit(e.formData)}>
          {/* Basic Fields Save Button */}
          <Flex justifyContent="flex-start" mb={4}>
            <SubmitButton isLoading={isLoading} />
          </Flex>
          
          {/* Handsontable with new functionality */}
          {allData.length > 0 && (
            <>
              {/* Status Messages */}
              <Flex justifyContent="space-between" mb={4} alignItems="center">
                <Box></Box>
                <HStack spacing={4}>
                  {changedRecords.size > 0 && (
                    <Box color="orange.500" fontSize="sm">
                      {changedRecords.size} record(s) modified - Click Save to update
                    </Box>
                  )}
                  {deletingRows.size > 0 && (
                    <Box color="red.500" fontSize="sm">
                      {deletingRows.size} record(s) being deleted...
                    </Box>
                  )}
                </HStack>
              </Flex>
              
              {/* Column Legend and Pagination */}
              <Flex justifyContent="flex-end"  mb={4} alignItems="center">
                {/* Column Legend */}
                
                
                {/* Pagination */}
                {totalRecords > pageSize && (
                  <Pagination 
                    handlePageChange={handlePageChange} 
                    currentPage={currentPage} 
                    totalPages={getTotalPages()} 
                  />
                )}
              </Flex>
              
              <HotTable
                ref={hotRef}
                {...hotSettings}
              />
              
              <ImageModal isModalOpen={isModalOpen} closeImageModal={closeImageModal} modalImageSrc={modalImageSrc} />
              
              {/* Table Save Button */}
              <Flex justifyContent="space-between" mt={4} alignItems="center">
                <SubmitButton onClick={saveClickCallback} isLoading={isSubmitting} />
                <HStack spacing={4}>
                  {changedRecords.size > 0 && (
                    <Box color="orange.500" fontSize="sm">
                      {changedRecords.size} record(s) modified - Click Save to update
                    </Box>
                  )}
                  {deletingRows.size > 0 && (
                    <Box color="red.500" fontSize="sm">
                      {deletingRows.size} record(s) being deleted...
                    </Box>
                  )}
                </HStack>
              </Flex>
            </>
          )}
        </Form>
      )}
      
      {/* Delete Confirmation Modal */}
      <Modal isOpen={isOpen} onClose={onClose}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Confirm Delete</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Text>
              Are you sure you want to delete this record?
            </Text>
            {rowToDelete && (
              <Box mt={3} p={3} bg="gray.50" borderRadius="md">
                <Text fontSize="sm" fontWeight="bold">Record Details:</Text>
                <Text fontSize="sm">ID: {rowToDelete.recordId}</Text>
                {/* Show first few fields for identification (adjust indices for S.No. column) */}
                {columnHeaders.slice(1, 4).map((header, index) => (
                  <Text key={index} fontSize="sm">
                    {header}: {rowToDelete.rowData[index + 2] || 'N/A'}
                  </Text>
                ))}
              </Box>
            )}
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onClose}>
              Cancel
            </Button>
            <Button 
              colorScheme="red" 
              onClick={handleConfirmedDelete}
              isLoading={rowToDelete && deletingRows.has(rowToDelete.rowIndex)}
            >
              Delete
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
};

export default Editorial;
